import { SelectOption as Option } from 'components/select/types';
import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Season } from 'models/seasons';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { translateSeason } from 'utils/translateAttribute';
import { BaseSelect } from '../../BaseSelect';

interface SeasonSelectProps {
  selectedSeasons?: string[];
  setSelectedSeasons?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}

const mapSeasonToSeasonOption = (season: Season, intl: IntlShape): Option<string> => {
  const { seasonCode } = season;
  const label = translateSeason(seasonCode, intl);

  return {
    value: seasonCode,
    label,
  };
};

export function SeasonSelect({
  selectedSeasons = undefined,
  setSelectedSeasons = undefined,
  className = undefined,
  showConfirmButton = false,
}: SeasonSelectProps) {
  const intl = useIntl();
  const { disabledFilters } = useTransitiveCacheStore();

  const { allSeasons } = useTransitiveCacheStore();
  const allSeasonOptions = useMemo(
    () =>
      allSeasons
        .map(c => mapSeasonToSeasonOption(c, intl))
        .sort((a, b) => {
          if (a.label && b.label) {
            return a.label.localeCompare(b.label);
          }

          return 0;
        }),
    [allSeasons, intl]
  );

  const { seasons, setSeasons } = useAppStore();

  return (
    <BaseSelect
      disabled={disabledFilters.includes('prdSeason')}
      id="season-filter"
      className={className}
      label={intl.formatMessage({
        id: 'filter.key.selectedSeasons',
        defaultMessage: 'Seasons',
      })}
      selectedItems={selectedSeasons ?? seasons ?? []}
      allOptionItems={allSeasonOptions}
      setSelectedItems={setSelectedSeasons ?? setSeasons}
      showConfirmButton={showConfirmButton}
    />
  );
}
